/**
 * Client enum mirrors for ZRF-FIN ledger domain (ZRF-FIN-002).
 * Aligns with BE `services/financials/constants.js` and
 * docs/requirements/quickbooks-subledger/05_DATA_MODEL.md.
 * No Stripe/QuickBooks API calls — constants only for future screens.
 */

/** @type {readonly string[]} */
export const SUPPORTED_CURRENCIES = Object.freeze(['CAD', 'USD']);

/** Default currency for ZenRentFlow financials. */
export const DEFAULT_CURRENCY = 'CAD';

/** @type {readonly string[]} */
export const RENT_SCHEDULE_STATUSES = Object.freeze([
  'draft',
  'scheduled',
  'due',
  'partially_paid',
  'paid',
  'overdue',
  'cancelled',
  'voided',
]);

/** @type {readonly string[]} */
export const RENT_SCHEDULE_LINE_TYPES = Object.freeze([
  'rent',
  'utilities',
  'parking',
  'garage',
  'pet_fee',
  'late_fee',
  'other_charge',
]);

/** @type {readonly string[]} */
export const LEDGER_ENTRY_TYPES = Object.freeze([
  'charge',
  'payment',
  'credit',
  'refund',
  'deposit_collected',
  'deposit_applied',
  'deposit_refunded',
  'adjustment',
  'void',
  'reversal',
]);

/** @type {readonly string[]} */
export const PAYMENT_METHOD_TYPES = Object.freeze([
  'interac_etransfer',
  'cash',
  'cheque',
  'bank_deposit',
  'stripe_card',
  'stripe_acss_debit',
  'pad_eft',
  'other',
]);

/** @type {readonly string[]} */
export const PAYMENT_STATUSES = Object.freeze([
  'draft',
  'pending',
  'posted',
  'failed',
  'refunded',
  'partially_refunded',
  'voided',
]);

/** @type {readonly string[]} */
export const DEPOSIT_TYPES = Object.freeze([
  'security_deposit',
  'pet_deposit',
  'key_deposit',
  'other_refundable_deposit',
]);

/** @type {readonly string[]} */
export const DEPOSIT_STATUSES = Object.freeze([
  'expected',
  'partially_collected',
  'collected',
  'partially_applied',
  'applied',
  'partially_refunded',
  'refunded',
  'disputed',
  'voided',
]);

/** @type {readonly string[]} */
export const FINANCIAL_EVENT_TYPES = Object.freeze([
  'ledger_created_from_lease',
  'charge_created',
  'payment_created',
  'payment_allocated',
  'payment_voided',
  'receipt_generated',
  'deposit_created',
  'deposit_collected',
  'deposit_applied',
  'deposit_refunded',
  'balance_recalculated',
  'stripe_webhook_received',
  'accounting_sync_started',
  'accounting_sync_completed',
  'accounting_sync_failed',
]);

/** @type {readonly string[]} */
export const FINANCIAL_ACTOR_TYPES = Object.freeze([
  'landlord',
  'tenant',
  'system',
  'integration',
]);

/** @type {readonly string[]} */
export const SCHEDULE_CREATED_FROM = Object.freeze([
  'lease',
  'manual',
  'recurring_rule',
  'import',
]);

/** @type {readonly string[]} */
export const ALLOCATION_TYPES = Object.freeze(['automatic', 'manual']);

/**
 * @param {unknown} value
 * @param {readonly string[]} allowed
 * @returns {boolean}
 */
export function isValidEnum(value, allowed) {
  return typeof value === 'string' && allowed.includes(value);
}
